import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2, ViewChild, Input } from '@angular/core';
import { Router } from '@angular/router';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';
import { Helpers } from '../../../helpers/helpers';
import { ElementService } from '../../../services/element.service';
import { LanguageApp } from '../../../config/datatable-config';
import { ToastrService } from 'ngx-toastr';
import { Status } from '../../../enums/status';
import { ElementType } from 'src/app/enums/element';
import { FeriaService } from 'src/app/services/feria.service';
import { SharedService } from 'src/app/services/shared.service';
import { StihlFeriaOrderFilterDTO } from 'src/app/models/stihl-feria-dto';

@Component({
  selector: 'app-feria-order-table',
  templateUrl: './feria-order-table.component.html'
})
export class FeriaOrderTableComponent implements OnInit {

  // Flags para ocultar columnas en la vista cliente ('mi-area'). En zona-stihl siempre se muestran.
  readonly SHOW_WOOD_SCULPTURES_COLUMN_TO_CLIENT = true;
  readonly SHOW_STATUS_COLUMN_TO_CLIENT = true;

  @Input() origin: string;
  @Input() idDistributor: string;
  idStatus: string = '0';
  @ViewChild(DataTableDirective, { static: false })
  dtElement: DataTableDirective;
  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  listenerFn!: () => void;
  public distributorsList: any[] = [];
  public statusList: any[] = [];

  constructor(private feriaService: FeriaService, private router: Router,
    private renderer: Renderer2,
    private toastr: ToastrService,
    private sharedService: SharedService,
    private helper: Helpers) { }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
    this.listenerFn = this.renderer.listen('document', 'click', (event) => {
      if (event.target.hasAttribute("edit-feria-order-id")) {
        if (this.origin == 'mi-area')
          this.router.navigate(["/myarea/orders-fairs/edit/" + event.target.getAttribute("edit-feria-order-id")]);
        else
          this.router.navigate(["/area-stihl/orders-fairs/edit/" + event.target.getAttribute("edit-feria-order-id")]);
      } else if (event.target.hasAttribute("delete-feria-order-id")) {
        this.delete(event.target.getAttribute("delete-feria-order-id"));
      }
    });
    this.helper.setDataTableSearchDelay();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
    if (this.listenerFn) {
      this.listenerFn();
    }
  }

  get showWoodSculpturesColumn(): boolean {
    return this.origin != 'mi-area' || this.SHOW_WOOD_SCULPTURES_COLUMN_TO_CLIENT;
  }

  get showStatusColumn(): boolean {
    return this.origin != 'mi-area' || this.SHOW_STATUS_COLUMN_TO_CLIENT;
  }

  rerender(): void {
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      dtInstance.draw();
    });
  }

  ngOnInit(): void {
    this.getDistris();
    this.getStatusList();
    this.setDatatable();
  }


  setDatatable() {
    const that = this;
    this.dtOptions = {
      responsive: true,
      paging: true,
      pageLength: 10,
      ordering: true,
      lengthChange: true,
      info: false,
      serverSide: true,
      processing: true,
      order: [[0, 'desc'],[3, 'asc']],
      language: LanguageApp.spanish_datatables,
      ajax: (dataTablesParameters: any, callback) => {
        that.feriaService.getDataTableOrder(dataTablesParameters, that.getFilter()).subscribe(resp => {
          callback({
            recordsTotal: resp.recordsTotal,
            recordsFiltered: resp.recordsFiltered,
            data: resp.data
          });
        }, (err) => {
          Swal.fire({
            icon: 'error',
            title: 'Erro ao recuperar os dados',
            text: 'Ocorreu um erro'
          });

        });
      },
      drawCallback: function () {
        that.helper.enableBsTooltips();
      },
      columns: this.getColumns(),
      columnDefs: [
        {
          targets: [this.getColumns().length - 1],
          orderable: false,
          data: "IdOrder",
          render: function (data, type, full) {
            var retorno = "";
            retorno += '<button edit-feria-order-id="' + data + '" class="btn-transparent btn-sm btn-icon"><div class="svg-icon"><svg viewBox="0 0 150 150"><use xlink:href="assets/images/icons/ico_eye_auto.svg#img" href="assets/images/icons/ico_eye_auto.svg#img"></use></svg></div> Ver</button>';
            if (that.origin == 'zona-stihl')
              retorno += '<button delete-feria-order-id="' + data + '"class="btn-transparent btn-sm btn-icon"><div class="svg-icon"><svg viewBox="0 0 150 150"><use xlink:href="assets/images/icons/ico_trash_auto.svg#img" href="assets/images/icons/ico_trash_auto.svg#img"></use></svg></div> Eliminar</button>';
            return retorno;
          }
        }
      ],
      initComplete: function () {
        that.helper.animateDataTable(this[0])
      }
    };
  }
  getColumns(): any[] {
    const columns = [
      { data: "CreationDate", className: "text-center", name: "CreationDate" },
      { data: "Account", name: "Account", orderable: false },
      { data: "Distributor", name: "Distributor", orderable: false },
      { data: "InitDate", name: "InitDate" },
      { data: "Feria", name: "Feria", orderable: false },
      { data: "Contact", name: "Contact", orderable: false },
      { data: "Email", name: "Email", orderable: false },
      { data: "Phone", name: "Phone", orderable: false },
    ];

    if (this.showWoodSculpturesColumn)
      columns.push({ data: "WoodSculptures", name: "WoodSculptures", orderable: false });

    if (this.showStatusColumn)
      columns.push({ data: "Status", name: "Status", orderable: false });

    columns.push({ data: "IdOrder", name: "IdOrder", orderable: false });

    return columns;
  }

  delete(id: string) {
    Swal.fire({
      title: 'Deseja remover este item?',
      text: "O item irá desaparecer ao administrador",
      icon: 'warning',
      buttonsStyling: false,
      customClass: {
        cancelButton: 'btn btn-primary me-2',
        confirmButton: 'btn btn-outline-secondary'
      },
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: '<i class="fa fa-trash-o" aria-hidden="true"></i> Eliminar',
      cancelButtonText: '<i class="fa fa-times" aria-hidden="true"></i> Cancelar',
      cancelButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true
    }).then((result) => {
      if (result.value) {
        this.feriaService.changeStatusOrder(id, Status.Baja).subscribe(() => {
          this.toastr.success("O item foi removido com sucesso");
          this.rerender();
        }, (err) => {
          Swal.fire({
            icon: 'error',
            title: 'Erro ao eliminar o item',
            text: 'Ocorreu um erro'
          });
        });
      }
    })
  }
  getFilter(): StihlFeriaOrderFilterDTO {
    return {
      IdDistributor: this.idDistributor == '0' ? null : +this.idDistributor,
      IdStatus: this.idStatus == '0' ? null : +this.idStatus
    };
  }

  downloadExcel() {
    Swal.fire({
      allowOutsideClick: false,
      icon: 'info',
      text: 'Aguarde por favor…'
    });
    Swal.showLoading();

    this.feriaService.getDataTableOrderExcel(this.getFilter()).subscribe(blob => {
      Swal.close();
      saveAs(blob, 'Pedidos-Feira.xlsx');
    }, (err) => {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao aceder á Base de Dados',
        text: 'Ocorreu um erro'
      });
    });
  }

  getDistris() {

    this.sharedService.getStihlDistributors().subscribe(res => {

      res.unshift({ value: '0', label: 'TODOS OS CONCESSIONÁRIOS' });
      this.distributorsList = res;


    });
  }

  getStatusList() {
    this.sharedService.getStihlCustomStatus([Status.Pendiente, Status.ContactoComEscultor, Status.EnvioDeKitDeEventos, Status.Concluido]).subscribe(res => {

      res.unshift({ value: '0', label: 'TODOS OS ESTADOS' });
      this.statusList = res;

    });
  }

}
